import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useCurrentLanguage } from '../hooks/useTranslation'

/**
 * Marketing 404 UI. Worker already returns HTTP 404 + X-Robots-Tag for unknown paths;
 * this reinforces noindex when React mounts on a missing client-side route.
 */
export default function NotFound() {
  const language = useCurrentLanguage()
  const langPrefix = language === 'pt' ? '/pt' : language === 'en' ? '/en' : ''

  useEffect(() => {
    document.title =
      language === 'pt'
        ? 'Página não encontrada | VIVA Psychotherapy'
        : 'Page not found | VIVA Psychotherapy'

    const upsert = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`)
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute('name', name)
        document.head.appendChild(el)
      }
      el.setAttribute('content', content)
    }
    upsert('robots', 'noindex, nofollow')
    upsert('googlebot', 'noindex, nofollow')

    const canonical = document.querySelector('link[rel="canonical"]')
    if (canonical) canonical.setAttribute('href', 'https://vivapsychotherapy.com/')
  }, [language])

  const title =
    language === 'pt' ? 'Página não encontrada' : 'Page not found'
  const body =
    language === 'pt'
      ? 'O endereço que você procura não existe ou foi movido.'
      : 'The page you are looking for does not exist or has been moved.'
  const homeLabel = language === 'pt' ? 'Ir para a página inicial' : 'Go to homepage'
  const teamLabel = language === 'pt' ? 'Ver a equipe' : 'View our team'
  const contactLabel = language === 'pt' ? 'Contato' : 'Contact'

  return (
    <div className="min-h-[60vh] bg-primary-offWhite flex items-center">
      <main className="max-w-xl mx-auto px-4 sm:px-6 py-16 text-center">
        <p className="text-sm font-semibold text-primary-darkGreen tracking-wide mb-2">404</p>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{title}</h1>
        <p className="text-gray-600 mb-8">{body}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={langPrefix || '/'}
            className="inline-flex justify-center px-5 py-2.5 rounded-lg bg-primary-darkGreen text-white font-semibold hover:opacity-90"
          >
            {homeLabel}
          </Link>
          <Link
            to={`${langPrefix}/team`}
            className="inline-flex justify-center px-5 py-2.5 rounded-lg border border-gray-300 text-gray-800 font-semibold hover:bg-white"
          >
            {teamLabel}
          </Link>
          <Link
            to={`${langPrefix}/contact`}
            className="inline-flex justify-center px-5 py-2.5 rounded-lg border border-gray-300 text-gray-800 font-semibold hover:bg-white"
          >
            {contactLabel}
          </Link>
        </div>
        <p className="mt-8 text-sm text-gray-500">
          <Link to={`${langPrefix}/sitemap`} className="text-primary-darkGreen hover:underline">
            {language === 'pt' ? 'Mapa do site' : 'Sitemap'}
          </Link>
        </p>
      </main>
    </div>
  )
}
